import { Alert, Box, Button, CircularProgress, Paper, Typography } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { handleApiError } from "../../../utils/errorHandler";
import { pushNotification } from "../../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider";
import { NotificationTypes } from "../../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider.types";
import { getVideoSettingsControl, updateVideoSettingsControl } from "../../../api/services/video";

interface ControlSettingsDrawerContentContentProps {
    closeToggle: () => void;
}

type PlayerSetting = {
    id: string;
    name: string;
    active: boolean;
};

const ControlSettingsDrawerContentContent: React.FC<ControlSettingsDrawerContentContentProps> = ({
    closeToggle
}) => {
    const [isApplyChangesLoading, setIsApplyChangesLoading] = useState<boolean>(false);
    const [playerSettings, setPlayerSettings] = useState<PlayerSetting[]>([]);
    const [draftSettings, setDraftSettings] = useState<PlayerSetting[]>([]);

    const fetchVideoControls = useCallback(async () => {
        try {
            const playerSettingsResponse = await getVideoSettingsControl();
            const playerControls = playerSettingsResponse.data.values;
            const players: PlayerSetting[] = [
                {
                    id: "videoUrl",
                    name: "Rumble",
                    active: playerControls.videoUrl,
                },
                {
                    id: "videoUrl1",
                    name: "Odysee",
                    active: playerControls.videoUrl1,
                },
                {
                    id: "videoUrl2",
                    name: "Vimeo",
                    active: playerControls.videoUrl2,
                },
            ];

            setPlayerSettings(players);
            setDraftSettings(players);
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({
                        details: msg,
                        type: NotificationTypes.Error,
                    });
                },
            });
        }
    }, []);

    // Initial state when component load
    useEffect(() => {
        const run = async () => {
            try {
                await fetchVideoControls();
            } catch (error) {
                handleApiError(error, {
                    notify: (msg) => {
                        pushNotification({
                            details: msg,
                            type: NotificationTypes.Error,
                        });
                    },
                });
            } finally {
                setIsApplyChangesLoading(false);
            }
        };
        run();
    }, []);

    const handleSubmitVideoSettings = async () => {
        setIsApplyChangesLoading(true);

        try {
            const payload = Object.fromEntries(
                draftSettings.map(player => [player.id, player.active])
            );

            const updateControlsResponse = await updateVideoSettingsControl(payload);

            pushNotification({
                details: updateControlsResponse.message,
                type: NotificationTypes.Success,
            });

            closeToggle();
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({
                        details: msg,
                        type: NotificationTypes.Error,
                    });
                },
            });
        } finally {
            setIsApplyChangesLoading(false);
        }
    };

    const handleTogglePlayer = (id: string) => {
        setDraftSettings(prev =>
            prev.map(player =>
                player.id === id
                    ? { ...player, active: !player.active }
                    : player
            )
        );
    };

    const hasChanges = JSON.stringify(playerSettings) !== JSON.stringify(draftSettings);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Top content and scrollable drag list */}
            <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 2, overflow: 'hidden' }}>
                <Box sx={{ pb: 1 }}>
                    <Alert severity="info" icon={false} sx={{ backgroundColor: '#E5F8FE', color: '#26C6F9' }}>
                        <Typography variant="subtitle2">
                            Select which video players are available in the student app.
                        </Typography>
                    </Alert>
                </Box>
                {draftSettings.map((draftPlayer) => {
                    const savedPlayer = playerSettings.find(
                        (p) => p.id === draftPlayer.id
                    );

                    const isPending =
                        savedPlayer?.active !== draftPlayer.active;

                    return (
                        <Paper
                            key={draftPlayer.id}
                            elevation={draftPlayer.active ? 3 : 0}
                            onClick={() => handleTogglePlayer(draftPlayer.id)}
                            sx={{
                                p: 2,
                                cursor: "pointer",
                                borderRadius: 2,
                                border: draftPlayer.active
                                    ? "2px solid #1565C0"
                                    : "2px solid #E0E0E0",
                                backgroundColor: draftPlayer.active
                                    ? "#F5FAFF"
                                    : "#FFFFFF",
                                transition: "all .2s ease",
                                "&:hover": {
                                    borderColor: "#1565C0",
                                    transform: "translateY(-2px)",
                                },
                            }}
                        >
                            <Box
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <Box>
                                    <Typography
                                        variant="subtitle1"
                                        fontWeight={600}
                                    >
                                        {draftPlayer.name}
                                    </Typography>

                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        {isPending
                                            ? draftPlayer.active
                                                ? "Will be activated after applying changes"
                                                : "Will be deactivated after applying changes"
                                            : draftPlayer.active
                                                ? "Currently active in student app"
                                                : "Currently inactive"}
                                    </Typography>
                                </Box>

                                <Box
                                    sx={{
                                        px: 1.5,
                                        py: 0.5,
                                        borderRadius: 10,
                                        fontSize: 12,
                                        fontWeight: 600,
                                        color: isPending
                                            ? "#ED6C02"
                                            : draftPlayer.active
                                                ? "#1565C0"
                                                : "#757575",
                                        backgroundColor: isPending
                                            ? "#FFF3E0"
                                            : draftPlayer.active
                                                ? "#E3F2FD"
                                                : "#F5F5F5",
                                    }}
                                >
                                    {isPending
                                        ? "PENDING"
                                        : draftPlayer.active
                                            ? "ACTIVE"
                                            : "INACTIVE"}
                                </Box>
                            </Box>
                        </Paper>
                    );
                })}
            </Box>

            <Box sx={{ pt: 2 }}>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSubmitVideoSettings}
                    disabled={!hasChanges || isApplyChangesLoading}
                    fullWidth
                    startIcon={isApplyChangesLoading ? <CircularProgress size={20} color="inherit" /> : null}
                >
                    APPLY CHANGES
                </Button>
            </Box>
        </Box>
    );
};

export default ControlSettingsDrawerContentContent;